import React from "react";
import HtmlText from "components/HtmlText";
import { GlobalEntityType, TimestampType } from "shared/types";
import { getComplexValueStepText } from "pages/Recipe/components/ComplexValueParameter/utils";
import { addLiquidTypes, basedOnTypes, bleedTypes, cellConcentratedExchangeTypes, conditionOperationTypes, consumptionBasedTypes, diluteTypes, DOControllerStepTypes, eventValueTypes, feedProcessTypes, feedTypes, fillTypes, findDictionaryItemByValue, fromCurrentTypes, gasProcessFlowTypes, getDefaultUnitsPrefix, inducerTypes, inoculationTypes, mediaExchangeTypes, operationTypes, parameterUnitsSearchVariants, PhControllerTypes, recipeParameterUnitsType, samplingTypes, stirProcessFlowTypes, takeSampleTypes, timeChangeTypes, waitTypes, } from "shared/rsDictionary";
import { isNotEmpty } from "utils/validate";
import { AddLiquidType, BasedOnType, BaseProcessFlowType, BleedType, CellConcentratedExchangeType, CellConcentratedLiquidType, ComplexValueParameterType, ConditionOperationType, CustomVariableFormulaType, CustomVariableRelativeValueVariableType, CustomVariableValueType, DiluteType, DOControllerStepType, EventValueType, FeedProcessType, FeedType, GasProcessFlowBasedOnType, InterventionType, LoopConditionType, MediaExchangeType, NoUnitsType, OperationType, ParameterTypesCategoryType, PhControllerType, RateByType, RecipeDialogType, RecipeStepType, SamplingType, StirProcessFlowBasedOnType, TakeSampleType, TimeProfileType, WaitType, } from "./recipe.types";
import { getTimeProfileStepText } from "pages/Recipe/components/RecipeBuilder/components/TimeProfile/utils";
import { parseComplexUnitToHtml } from "./custom-variables.utils";
import { getNutrientsProcessVariables, getSelectedProcessVariable } from "store/global/global.utils";
import { OptimizationParameterParameterType, } from "store/rs-workspace/rs-workspace.types";
import { getRecipeParametersAndAlternativeStepsOptions } from "store/rs-workspace/utils/common.utils";
const getAddLiquidFillStepText = (addLiquidTypeProperties, parameters, customVariables) => {
    const { fillType, value: volume } = addLiquidTypeProperties;
    const fillTypeLabel = findDictionaryItemByValue(fillTypes, fillType)?.label;
    if (!fillTypeLabel) {
        return;
    }
    return getComplexValueStepText({ value: volume, parameters, customVariables, prefix: fillTypeLabel });
};
const getAddLiquidInoculationStepText = (addLiquidTypeProperties, parameters, customVariables) => {
    const { type, value } = { ...addLiquidTypeProperties?.addLiquidProperties };
    const inoculationTypeLabel = findDictionaryItemByValue(inoculationTypes, type)?.label;
    if (!inoculationTypeLabel) {
        return;
    }
    return getComplexValueStepText({ value, parameters, customVariables, prefix: inoculationTypeLabel });
};
const getAddLiquidInducerStepText = (addLiquidTypeProperties, parameters, customVariables) => {
    const { type, value } = { ...addLiquidTypeProperties?.value };
    const inducerTypeLabel = findDictionaryItemByValue(inducerTypes, type)?.label;
    if (!inducerTypeLabel) {
        return;
    }
    return getComplexValueStepText({ value, parameters, customVariables, prefix: inducerTypeLabel });
};
const getAddLiquidDiluteStepText = (addLiquidTypeProperties, parameters, customVariables) => {
    const { type, cellDensity, time } = { ...addLiquidTypeProperties?.properties };
    const diluteTypeLabel = findDictionaryItemByValue(diluteTypes, type)?.label;
    if (!diluteTypeLabel) {
        return;
    }
    const cellDensityLabel = getComplexValueStepText({
        value: cellDensity,
        parameters,
        customVariables,
        prefix: diluteTypeLabel,
    });
    switch (type) {
        case DiluteType.ACHIEVE:
            return cellDensityLabel;
        case DiluteType.MAINTAIN:
            return getComplexValueStepText({
                value: time,
                parameters,
                customVariables,
                prefix: React.createElement(React.Fragment, null,
                    cellDensityLabel,
                    " for"),
            });
    }
};
const getAddLiquidFeedStepText = (addLiquidTypeProperties, parameters, customVariables, processVariables) => {
    const { type, component, amount, concentration, fromCurrent, timeProfile, consumptionType, feedProcessType, cellDensity, time, volume, timeCollectData, maintainTime, } = {
        ...addLiquidTypeProperties?.properties,
    };
    const feedTypeLabel = findDictionaryItemByValue(feedTypes, type)?.label;
    const componentName = getNutrientsProcessVariables(processVariables)?.find(({ key }) => key === component)?.name;
    if (!feedTypeLabel) {
        return;
    }
    switch (type) {
        case FeedType.AMOUNT_CONSTITUENT: {
            if (!componentName || !timeProfile) {
                return;
            }
            const amountValueLabel = getComplexValueStepText({
                value: amount,
                parameters,
                customVariables,
                prefix: feedTypeLabel,
            });
            if (!amountValueLabel) {
                return;
            }
            return getTimeProfileStepText(timeProfile, parameters, customVariables, React.createElement(React.Fragment, null,
                amountValueLabel,
                ", ",
                componentName,
                ","));
        }
        case FeedType.CONCENTRATION_CONSTITUENT: {
            if (!componentName || !timeProfile) {
                return;
            }
            const concentrationValueLabel = getComplexValueStepText({
                value: concentration,
                parameters,
                customVariables,
                prefix: feedTypeLabel,
            });
            const fromCurrentLabel = findDictionaryItemByValue(fromCurrentTypes, fromCurrent)?.label;
            if (!concentrationValueLabel || !fromCurrentLabel) {
                return;
            }
            return getTimeProfileStepText(timeProfile, parameters, customVariables, React.createElement(React.Fragment, null,
                concentrationValueLabel,
                ", ",
                componentName,
                ", ",
                fromCurrentLabel,
                ","));
        }
        case FeedType.MAINTAIN_CONSTITUENT: {
            const consumptionTypeLabel = findDictionaryItemByValue(consumptionBasedTypes, consumptionType)?.label;
            const timeCollectDataLabel = getComplexValueStepText({
                value: timeCollectData,
                parameters,
                customVariables,
                prefix: "Time to collect data",
            });
            const maintainTimeLabel = getComplexValueStepText({
                value: maintainTime,
                parameters,
                customVariables,
                prefix: "Time to maintain",
            });
            if (!componentName || !consumptionType || !maintainTimeLabel || !timeCollectDataLabel) {
                return;
            }
            return getComplexValueStepText({
                value: concentration,
                parameters,
                customVariables,
                prefix: (React.createElement(React.Fragment, null,
                    feedTypeLabel,
                    ", ",
                    componentName,
                    ", ",
                    timeCollectDataLabel,
                    ", ",
                    maintainTimeLabel,
                    ",",
                    " ",
                    consumptionTypeLabel,
                    ",")),
            });
        }
        case FeedType.CELL_DENSITY: {
            const feedProcessTypeLabel = findDictionaryItemByValue(feedProcessTypes, feedProcessType)?.label;
            if (!feedProcessTypeLabel) {
                return;
            }
            const cellDensityValueLabel = getComplexValueStepText({
                value: cellDensity,
                parameters,
                customVariables,
                prefix: (React.createElement(React.Fragment, null,
                    feedTypeLabel,
                    ", ",
                    feedProcessTypeLabel)),
            });
            if (!cellDensityValueLabel) {
                return;
            }
            switch (feedProcessType) {
                case FeedProcessType.ACHIEVE:
                    return cellDensityValueLabel;
                case FeedProcessType.MAINTAIN:
                    return getComplexValueStepText({
                        value: time,
                        parameters,
                        customVariables,
                        prefix: React.createElement(React.Fragment, null,
                            cellDensityValueLabel,
                            " for"),
                    });
            }
            return;
        }
        case FeedType.VOLUME:
        case FeedType.TO_VOLUME: {
            const volumeValueLabel = getComplexValueStepText({
                value: volume,
                parameters,
                customVariables,
                prefix: feedTypeLabel,
            });
            if (!volumeValueLabel) {
                return;
            }
            return getTimeProfileStepText(timeProfile, parameters, customVariables, React.createElement(React.Fragment, null,
                volumeValueLabel,
                ", "));
        }
    }
};
const getStepCustomVariableSummaryText = (waitTypeLabel, eventValueTypeLabel, customVariables, customVariableId, eventValueValue) => {
    if (!!customVariableId &&
        eventValueValue?.type !== ComplexValueParameterType.RELATIVE_VALUES &&
        eventValueValue?.type !== ComplexValueParameterType.PARAMETER) {
        const customVariable = customVariables?.find(({ id }) => id === customVariableId);
        const unit = parseComplexUnitToHtml(customVariable?.formula?.configuredUnits?.complexValue)?.html;
        const customVariableAsValue = customVariables?.find(({ id }) => id === eventValueValue?.variableId);
        const suffix = eventValueValue?.type === ComplexValueParameterType.CUSTOM_VARIABLE
            ? `{${customVariableAsValue?.name}}`
            : `${eventValueValue?.value} ${unit}`;
        return `${waitTypeLabel} ${eventValueTypeLabel} ${suffix}`;
    }
    return "";
};
export const getStepText = (step, parameters, customVariables, processVariables) => {
    if (!step.isCompleted) {
        return;
    }
    switch (step.type) {
        case RecipeStepType.TIMESTAMP:
            return step.text;
        case RecipeStepType.ADD_LIQUID: {
            const { addLiquidTypeProperties } = step;
            switch (addLiquidTypeProperties?.type) {
                case AddLiquidType.FILL:
                    return getAddLiquidFillStepText(addLiquidTypeProperties, parameters, customVariables);
                case AddLiquidType.INOCULATION:
                    return getAddLiquidInoculationStepText(addLiquidTypeProperties, parameters, customVariables);
                case AddLiquidType.INDUCER:
                    return getAddLiquidInducerStepText(addLiquidTypeProperties, parameters, customVariables);
                case AddLiquidType.DILUTE:
                    return getAddLiquidDiluteStepText(addLiquidTypeProperties, parameters, customVariables);
                case AddLiquidType.FEED:
                    return getAddLiquidFeedStepText(addLiquidTypeProperties, parameters, customVariables, processVariables);
                default:
                    return;
            }
        }
        case RecipeStepType.WAIT: {
            const { properties } = step;
            let waitTypeLabel;
            if (properties?.type === WaitType.FOR_TIME) {
                waitTypeLabel = findDictionaryItemByValue(waitTypes, properties.type)?.label;
            }
            else {
                const processVariable = getSelectedProcessVariable(processVariables ?? [], properties?.variable);
                const customVariable = customVariables?.find(({ id }) => id === properties?.customVariable);
                if (processVariable) {
                    waitTypeLabel = `For ${processVariable.name}`;
                }
                if (customVariable) {
                    waitTypeLabel = `For {${customVariable.name}}`;
                }
            }
            if (!waitTypeLabel) {
                return;
            }
            switch (properties?.type) {
                case WaitType.FOR_TIME:
                    return getComplexValueStepText({
                        value: properties.time,
                        parameters,
                        customVariables,
                        prefix: properties.time?.type !== ComplexValueParameterType.INFINITE ? waitTypeLabel : undefined,
                        infiniteProps: { withPrefix: true, capitalizeFirstLetter: true },
                    });
                case WaitType.FOR_EVENT: {
                    const eventValueTypeLabel = findDictionaryItemByValue(eventValueTypes, properties.eventValue?.type)?.label?.toLowerCase();
                    switch (properties.eventValue?.type) {
                        case EventValueType.REACHES:
                        case EventValueType.GOES_OVER:
                        case EventValueType.GOES_UNDER: {
                            const baseText = getComplexValueStepText({
                                value: properties.eventValue.value,
                                parameters,
                                customVariables,
                                prefix: `${waitTypeLabel} ${eventValueTypeLabel}`,
                            });
                            const customText = getStepCustomVariableSummaryText(waitTypeLabel, eventValueTypeLabel, customVariables, properties?.customVariable, properties.eventValue.value);
                            return customText || baseText;
                        }
                        case EventValueType.OUT_OF_RANGE: {
                            const minBaseText = getComplexValueStepText({
                                value: properties.eventValue.min,
                                parameters,
                                customVariables,
                                prefix: `${waitTypeLabel} ${eventValueTypeLabel}`,
                            });
                            const minCustomText = getStepCustomVariableSummaryText(waitTypeLabel, eventValueTypeLabel, customVariables, properties?.customVariable, properties.eventValue.min);
                            const maxBaseText = getComplexValueStepText({
                                value: properties.eventValue.max,
                                parameters,
                                customVariables,
                                prefix: "-",
                            });
                            const maxCustomText = getStepCustomVariableSummaryText(waitTypeLabel, eventValueTypeLabel, customVariables, properties?.customVariable, properties.eventValue.max);
                            const minText = minCustomText || minBaseText;
                            const maxText = maxCustomText || maxBaseText;
                            if (minText && maxText) {
                                return (React.createElement(React.Fragment, null,
                                    minText,
                                    React.createElement("span", null, " "),
                                    maxText));
                            }
                            return;
                        }
                        default:
                            return;
                    }
                }
                default:
                    return;
            }
        }
        case RecipeStepType.SET_TEMPERATURE:
        case RecipeStepType.SET_DO:
        case RecipeStepType.SET_PH: {
            const checkShouldDisplayOffset = (value) => !!value;
            const getOffsetPrefix = (value) => (value && value > 0 ? "+" : "");
            const timeLabel = getComplexValueStepText({
                value: step?.properties?.time,
                parameters,
                customVariables,
            });
            switch (step?.properties?.type) {
                case OperationType.MAINTAIN: {
                    const valueLabel = getComplexValueStepText({
                        value: step.properties.value,
                        parameters,
                        customVariables,
                    });
                    const offsetLabel = getComplexValueStepText({
                        value: step.properties.offset,
                        parameters,
                        customVariables,
                        prefix: getOffsetPrefix,
                        validateValueFn: checkShouldDisplayOffset,
                    });
                    if (!valueLabel || !timeLabel) {
                        return;
                    }
                    return (React.createElement(React.Fragment, null,
                        valueLabel,
                        offsetLabel ? React.createElement(React.Fragment, null,
                            " (",
                            offsetLabel,
                            ")") : "",
                        " for ",
                        timeLabel));
                }
                case OperationType.RAMP: {
                    const fromLabel = getComplexValueStepText({
                        value: step.properties.from,
                        parameters,
                        customVariables,
                    });
                    const fromOffsetLabel = getComplexValueStepText({
                        value: step.properties.fromOffset,
                        parameters,
                        customVariables,
                        prefix: getOffsetPrefix,
                        validateValueFn: checkShouldDisplayOffset,
                    });
                    const toLabel = getComplexValueStepText({ value: step.properties.to, parameters, customVariables });
                    const toOffsetLabel = getComplexValueStepText({
                        value: step.properties.toOffset,
                        parameters,
                        customVariables,
                        prefix: getOffsetPrefix,
                        validateValueFn: checkShouldDisplayOffset,
                    });
                    if (!fromLabel || !toLabel || !timeLabel) {
                        return;
                    }
                    return (React.createElement(React.Fragment, null,
                        "From ",
                        fromLabel,
                        fromOffsetLabel ? React.createElement(React.Fragment, null,
                            " (",
                            fromOffsetLabel,
                            ")") : "",
                        " for ",
                        timeLabel,
                        " to",
                        React.createElement(React.Fragment, null, " "),
                        toLabel,
                        toOffsetLabel ? React.createElement(React.Fragment, null,
                            " (",
                            toOffsetLabel,
                            ")") : "",
                        " for ",
                        timeLabel));
                }
                default:
                    return;
            }
        }
        case RecipeStepType.STIR_BIOREACTOR:
        case RecipeStepType.GAS_BIOREACTOR: {
            const isGas = step.type === RecipeStepType.GAS_BIOREACTOR;
            const { properties: bioreactorProperties } = step;
            switch (bioreactorProperties?.type) {
                case BaseProcessFlowType.CONSTANT_RATE: {
                    const valueLabel = getComplexValueStepText({
                        value: bioreactorProperties?.rate,
                        parameters,
                        customVariables,
                    });
                    const timeLabel = getComplexValueStepText({
                        value: bioreactorProperties?.time,
                        parameters,
                        customVariables,
                    });
                    if (valueLabel && timeLabel) {
                        const typeLabel = `${isGas ? "Gassing" : "Stirring"} rate at`;
                        return (React.createElement(React.Fragment, null,
                            typeLabel,
                            " ",
                            valueLabel,
                            ", for ",
                            timeLabel));
                    }
                    return;
                }
                case BaseProcessFlowType.RAMP: {
                    const typeLabel = findDictionaryItemByValue(timeChangeTypes, bioreactorProperties?.properties?.type)?.label;
                    const startRateLabel = getComplexValueStepText({
                        value: bioreactorProperties?.properties?.startRate,
                        parameters,
                        customVariables,
                    });
                    const endRateLabel = getComplexValueStepText({
                        value: bioreactorProperties?.properties?.endRate,
                        parameters,
                        customVariables,
                    });
                    const rampTimeLabel = getComplexValueStepText({
                        value: bioreactorProperties?.properties?.time,
                        parameters,
                        customVariables,
                    });
                    if (typeLabel && startRateLabel && endRateLabel && rampTimeLabel) {
                        return (React.createElement(React.Fragment, null,
                            typeLabel,
                            ", Starting rate - ",
                            startRateLabel,
                            ", End rate - ",
                            endRateLabel,
                            ", for ",
                            rampTimeLabel));
                    }
                    return;
                }
                case StirProcessFlowBasedOnType.BASED_ON: {
                    const onTypeLabel = findDictionaryItemByValue(basedOnTypes, bioreactorProperties?.on?.type)?.label;
                    const onTimeLabel = getComplexValueStepText({
                        value: bioreactorProperties?.on?.time,
                        parameters,
                        customVariables,
                    });
                    if (!onTypeLabel || !onTimeLabel) {
                        return;
                    }
                    switch (bioreactorProperties?.on?.type) {
                        case BasedOnType.Ppv: {
                            const ppvValueLabel = getComplexValueStepText({
                                value: bioreactorProperties?.on?.ppv,
                                parameters,
                                customVariables,
                            });
                            if (!ppvValueLabel) {
                                return;
                            }
                            return (React.createElement(React.Fragment, null,
                                "Target ",
                                onTypeLabel,
                                " - ",
                                ppvValueLabel,
                                ", for ",
                                onTimeLabel));
                        }
                        case BasedOnType.Kla: {
                            const klaValueLabel = getComplexValueStepText({
                                value: bioreactorProperties?.on?.kla,
                                parameters,
                                customVariables,
                            });
                            if (!klaValueLabel) {
                                return;
                            }
                            return (React.createElement(React.Fragment, null,
                                "Target ",
                                onTypeLabel,
                                " - ",
                                klaValueLabel,
                                ", for ",
                                onTimeLabel));
                        }
                        default:
                            return;
                    }
                }
                case GasProcessFlowBasedOnType.BASED_ON_KLA: {
                    const onKlaLabel = findDictionaryItemByValue(basedOnTypes, BasedOnType.Kla)?.label;
                    const klaValueLabel = getComplexValueStepText({
                        value: bioreactorProperties?.kla,
                        parameters,
                        customVariables,
                    });
                    const klaTimeLabel = getComplexValueStepText({
                        value: bioreactorProperties?.time,
                        parameters,
                        customVariables,
                    });
                    if (!klaValueLabel || !onKlaLabel || !klaTimeLabel) {
                        return;
                    }
                    return (React.createElement(React.Fragment, null,
                        "Target ",
                        onKlaLabel,
                        " - ",
                        klaValueLabel,
                        ", for ",
                        klaTimeLabel));
                }
                default:
                    return;
            }
        }
        case RecipeStepType.SET_DO_CONTROLLER: {
            if (!step.isCompleted) {
                return;
            }
            const DODurationLabel = getComplexValueStepText({
                value: step.time,
                parameters,
                customVariables,
                infiniteProps: { withPrefix: true },
            });
            if (!DODurationLabel) {
                return;
            }
            const text = step.steps
                .map((step) => findDictionaryItemByValue(DOControllerStepTypes, step?.type)?.label ?? "")
                .join(" → ");
            return isNotEmpty(text, true) ? (React.createElement(React.Fragment, null,
                text,
                ". Duration: ",
                DODurationLabel)) : undefined;
        }
        case RecipeStepType.SET_PH_CONTROLLER: {
            if (!step.isCompleted) {
                return;
            }
            const { topControlProperties, bottomControlProperties, time: PhDuration } = { ...step };
            const labels = [
                { name: "Top", properties: topControlProperties },
                { name: "Bottom", properties: bottomControlProperties },
            ].reduce((acc, { name, properties: { enabled, controllerProperties } = {} }) => {
                if (!enabled) {
                    return acc;
                }
                const typeLabel = findDictionaryItemByValue(PhControllerTypes, controllerProperties?.type)?.label;
                return [...acc, `${name} control: ${typeLabel}`];
            }, []);
            if (!labels.length) {
                return;
            }
            const PhDurationLabel = getComplexValueStepText({
                value: PhDuration,
                parameters,
                customVariables,
                infiniteProps: { withPrefix: true },
            });
            return (React.createElement(React.Fragment, null,
                labels.map((label) => (React.createElement(React.Fragment, null,
                    label,
                    " ",
                    React.createElement("br", null)))),
                "Duration: ",
                PhDurationLabel));
        }
        case RecipeStepType.TAKE_SAMPLE: {
            const takeSampleType = step.properties?.type;
            const takeSampleTypeLabel = findDictionaryItemByValue(takeSampleTypes, takeSampleType)?.label;
            if (!takeSampleTypeLabel) {
                return;
            }
            switch (takeSampleType) {
                case TakeSampleType.VOLUME_REDUCTION:
                    return getComplexValueStepText({
                        value: step.properties?.volume,
                        parameters,
                        customVariables,
                        prefix: React.createElement(React.Fragment, null,
                            takeSampleTypeLabel,
                            " to"),
                    });
                case TakeSampleType.SAMPLING: {
                    const samplingType = step.properties?.properties?.type;
                    const samplingTypeLabel = findDictionaryItemByValue(samplingTypes, samplingType)?.label;
                    if (!samplingTypeLabel) {
                        return;
                    }
                    switch (samplingType) {
                        case SamplingType.BY_VOLUME:
                            return getComplexValueStepText({
                                value: step.properties?.properties?.volume,
                                parameters,
                                customVariables,
                                prefix: (React.createElement(React.Fragment, null,
                                    takeSampleTypeLabel,
                                    " ",
                                    samplingTypeLabel.toLowerCase(),
                                    " - ")),
                            });
                        case SamplingType.BY_CELLS_NUMBER:
                            return getComplexValueStepText({
                                value: step.properties?.properties?.number,
                                parameters,
                                customVariables,
                                prefix: (React.createElement(React.Fragment, null,
                                    takeSampleTypeLabel,
                                    " ",
                                    samplingTypeLabel.toLowerCase(),
                                    " - ")),
                            });
                        default:
                            return;
                    }
                }
                default:
                    return;
            }
        }
        case RecipeStepType.BLEED: {
            const bleedType = step.properties?.type;
            const bleedTypeLabel = findDictionaryItemByValue(bleedTypes, step.properties?.type)?.label;
            if (!bleedTypeLabel) {
                return;
            }
            switch (bleedType) {
                case BleedType.HOLD_CELL_DENSITY:
                case BleedType.HOLD_VOLUME: {
                    const bleedTimeLabel = getComplexValueStepText({
                        value: step.properties?.time,
                        parameters,
                        customVariables,
                    });
                    if (!bleedTimeLabel) {
                        return;
                    }
                    switch (bleedType) {
                        case BleedType.HOLD_CELL_DENSITY: {
                            const bleedDensityLabel = getComplexValueStepText({
                                value: step.properties?.density,
                                parameters,
                                customVariables,
                            });
                            if (!bleedDensityLabel) {
                                return;
                            }
                            return (React.createElement(React.Fragment, null,
                                bleedTypeLabel,
                                " - ",
                                bleedDensityLabel,
                                " for ",
                                bleedTimeLabel));
                        }
                        case BleedType.HOLD_VOLUME: {
                            const bleedVolumeLabel = getComplexValueStepText({
                                value: step.properties?.volume,
                                parameters,
                                customVariables,
                            });
                            if (!bleedVolumeLabel) {
                                return;
                            }
                            return (React.createElement(React.Fragment, null,
                                bleedTypeLabel,
                                " - ",
                                bleedVolumeLabel,
                                " for ",
                                bleedTimeLabel));
                        }
                        default:
                            return;
                    }
                }
                case BleedType.REMOVE_CELL_FREE_LIQUID:
                case BleedType.REMOVE_CELL_CONCENTRATED_LIQUID: {
                    const bleedTimeProfile = bleedType === BleedType.REMOVE_CELL_FREE_LIQUID
                        ? step.properties?.timeProfile
                        : step.properties?.properties?.timeProfile;
                    const bleedTimeProfileLabel = getTimeProfileStepText(bleedTimeProfile, parameters, customVariables);
                    if (!bleedTimeProfileLabel) {
                        return;
                    }
                    switch (bleedType) {
                        case BleedType.REMOVE_CELL_FREE_LIQUID: {
                            const bleedCellVolumeLabel = getComplexValueStepText({
                                value: step.properties?.volume,
                                parameters,
                                customVariables,
                            });
                            if (!bleedCellVolumeLabel) {
                                return;
                            }
                            return (React.createElement(React.Fragment, null,
                                bleedTypeLabel,
                                ", ",
                                bleedCellVolumeLabel,
                                ", ",
                                bleedTimeProfileLabel));
                        }
                        case BleedType.REMOVE_CELL_CONCENTRATED_LIQUID: {
                            switch (step.properties?.properties?.type) {
                                case CellConcentratedLiquidType.VOLUME: {
                                    const bleedCellVolumeLabel = getComplexValueStepText({
                                        value: step.properties?.properties?.volume,
                                        parameters,
                                        customVariables,
                                    });
                                    return (React.createElement(React.Fragment, null,
                                        bleedTypeLabel,
                                        ", ",
                                        bleedCellVolumeLabel,
                                        ", ",
                                        bleedTimeProfileLabel));
                                }
                                case CellConcentratedLiquidType.NUMBER_OF_CELLS: {
                                    const bleedCellNumberOfCellsLabel = getComplexValueStepText({
                                        value: step.properties?.properties?.numberOfCells,
                                        parameters,
                                        customVariables,
                                        valueSuffix: "cells",
                                    });
                                    return (React.createElement(React.Fragment, null,
                                        bleedTypeLabel,
                                        ", ",
                                        bleedCellNumberOfCellsLabel,
                                        ", ",
                                        bleedTimeProfileLabel));
                                }
                                default:
                                    return;
                            }
                        }
                        default:
                            return;
                    }
                }
                default:
                    return;
            }
        }
        case RecipeStepType.MEDIA_EXCHANGE: {
            const mediaExchangeType = step.properties?.type;
            const mediaExchangeTypeLabel = findDictionaryItemByValue(mediaExchangeTypes, mediaExchangeType)?.label;
            if (!mediaExchangeTypeLabel) {
                return;
            }
            switch (mediaExchangeType) {
                case MediaExchangeType.CELL_FREE_LIQUID:
                    return getComplexValueStepText({
                        value: step.properties?.volume,
                        parameters,
                        customVariables,
                        prefix: React.createElement(React.Fragment, null,
                            mediaExchangeTypeLabel,
                            ", "),
                    });
                case MediaExchangeType.CELL_CONCENTRATED: {
                    const cellConcentratedType = step.properties?.properties?.type;
                    const cellConcentratedTypeLabel = findDictionaryItemByValue(cellConcentratedExchangeTypes, cellConcentratedType)?.label;
                    if (!cellConcentratedTypeLabel) {
                        return;
                    }
                    switch (cellConcentratedType) {
                        case CellConcentratedExchangeType.BY_VOLUME:
                            return getComplexValueStepText({
                                value: step.properties?.properties?.volume,
                                parameters,
                                customVariables,
                                prefix: React.createElement(React.Fragment, null,
                                    mediaExchangeTypeLabel,
                                    ", "),
                            });
                        case CellConcentratedExchangeType.BY_CELL_NUMBER:
                            return getComplexValueStepText({
                                value: step.properties?.properties?.numberOfCells,
                                parameters,
                                customVariables,
                                prefix: React.createElement(React.Fragment, null,
                                    mediaExchangeTypeLabel,
                                    ", "),
                                valueSuffix: "cells",
                            });
                        default:
                            return;
                    }
                }
                default:
                    return;
            }
        }
        default:
            return;
    }
};
export const checkComplexValueUsed = (value, complexValueType, complexValueId) => {
    if (!isNotEmpty(complexValueId) || complexValueType !== value?.type) {
        return false;
    }
    if (value?.type === ComplexValueParameterType.PARAMETER) {
        return value.parameterId === complexValueId;
    }
    else if (value?.type === ComplexValueParameterType.CUSTOM_VARIABLE) {
        return value.variableId === complexValueId;
    }
    else {
        return false;
    }
};
const checkCustomVariableUsedInWaitEventStep = (eventWaitProperties, customVariableId) => eventWaitProperties?.customVariable === customVariableId;
export const checkComplexValueUsedInTimeProfile = (value, complexValueType, complexValueId) => {
    if (!isNotEmpty(complexValueId)) {
        return false;
    }
    switch (value?.type) {
        case TimeProfileType.CONSTANT_RATE:
            switch (value?.rateByProperties?.type) {
                case RateByType.BY_RATE:
                    return checkComplexValueUsed(value.rateByProperties.rate, complexValueType, complexValueId);
                case RateByType.BY_TIME:
                    return checkComplexValueUsed(value.rateByProperties.forTime, complexValueType, complexValueId);
                default:
                    return false;
            }
        case TimeProfileType.RAMP:
            return (checkComplexValueUsed(value.startRate, complexValueType, complexValueId) ||
                checkComplexValueUsed(value.value?.value, complexValueType, complexValueId));
        default:
            return false;
    }
};
export const getRecipeStepsUsingComplexValue = (steps, complexValueType, complexValueId) => steps.filter((step) => {
    switch (step.type) {
        case RecipeStepType.ADD_LIQUID:
            switch (step.addLiquidTypeProperties?.type) {
                case AddLiquidType.FILL:
                    return checkComplexValueUsed(step.addLiquidTypeProperties?.value, complexValueType, complexValueId);
                case AddLiquidType.INOCULATION:
                    return checkComplexValueUsed(step.addLiquidTypeProperties?.addLiquidProperties?.value, complexValueType, complexValueId);
                case AddLiquidType.INDUCER:
                    return checkComplexValueUsed(step.addLiquidTypeProperties?.value?.value, complexValueType, complexValueId);
                case AddLiquidType.DILUTE: {
                    const { type, cellDensity, time } = {
                        ...step.addLiquidTypeProperties?.properties,
                    };
                    switch (type) {
                        case DiluteType.MAINTAIN:
                            return [cellDensity, time].some((value) => checkComplexValueUsed(value, complexValueType, complexValueId));
                        case DiluteType.ACHIEVE:
                            return checkComplexValueUsed(cellDensity, complexValueType, complexValueId);
                        default:
                            return false;
                    }
                }
                case AddLiquidType.FEED: {
                    const { properties } = { ...step.addLiquidTypeProperties };
                    switch (properties?.type) {
                        case FeedType.AMOUNT_CONSTITUENT:
                            return (checkComplexValueUsed(properties.amount, complexValueType, complexValueId) ||
                                checkComplexValueUsedInTimeProfile(properties.timeProfile, complexValueType, complexValueId));
                        case FeedType.CONCENTRATION_CONSTITUENT:
                            return (checkComplexValueUsed(properties.concentration, complexValueType, complexValueId) ||
                                checkComplexValueUsedInTimeProfile(properties.timeProfile, complexValueType, complexValueId));
                        case FeedType.MAINTAIN_CONSTITUENT:
                            return checkComplexValueUsed(properties.concentration, complexValueType, complexValueId);
                        case FeedType.CELL_DENSITY:
                            return [properties.cellDensity, properties.time].some((value) => checkComplexValueUsed(value, complexValueType, complexValueId));
                        case FeedType.TO_VOLUME:
                        case FeedType.VOLUME:
                            return (checkComplexValueUsed(properties.volume, complexValueType, complexValueId) ||
                                checkComplexValueUsedInTimeProfile(properties.timeProfile, complexValueType, complexValueId));
                        default:
                            return false;
                    }
                }
                default:
                    return false;
            }
        case RecipeStepType.WAIT:
            switch (step.properties?.type) {
                case WaitType.FOR_TIME:
                    return checkComplexValueUsed(step.properties?.time, complexValueType, complexValueId);
                case WaitType.FOR_EVENT:
                    switch (step.properties?.eventValue?.type) {
                        case EventValueType.REACHES:
                        case EventValueType.GOES_OVER:
                        case EventValueType.GOES_UNDER:
                            return (checkCustomVariableUsedInWaitEventStep(step.properties, complexValueId) ||
                                checkComplexValueUsed(step.properties.eventValue.value, complexValueType, complexValueId));
                        case EventValueType.OUT_OF_RANGE:
                            return (checkCustomVariableUsedInWaitEventStep(step.properties, complexValueId) ||
                                [step.properties.eventValue.min, step.properties.eventValue.max].some((value) => checkComplexValueUsed(value, complexValueType, complexValueId)));
                        default:
                            return false;
                    }
                default:
                    return false;
            }
        case RecipeStepType.SET_TEMPERATURE:
        case RecipeStepType.SET_DO:
        case RecipeStepType.SET_PH: {
            const { properties } = { ...step };
            switch (properties?.type) {
                case OperationType.MAINTAIN:
                    return [properties.value, properties.offset, properties.time].some((value) => checkComplexValueUsed(value, complexValueType, complexValueId));
                case OperationType.RAMP:
                    return [
                        properties.to,
                        properties.toOffset,
                        properties.from,
                        properties.fromOffset,
                        properties.time,
                    ].some((value) => checkComplexValueUsed(value, complexValueType, complexValueId));
                default:
                    return false;
            }
        }
        case RecipeStepType.STIR_BIOREACTOR:
        case RecipeStepType.GAS_BIOREACTOR: {
            const { properties: bioreactorProperties } = { ...step };
            switch (bioreactorProperties?.type) {
                case BaseProcessFlowType.CONSTANT_RATE:
                    return [bioreactorProperties.rate, bioreactorProperties.time].some((value) => checkComplexValueUsed(value, complexValueType, complexValueId));
                case BaseProcessFlowType.RAMP:
                    return [
                        bioreactorProperties.properties.startRate,
                        bioreactorProperties.properties.endRate,
                        bioreactorProperties.properties.time,
                    ].some((value) => checkComplexValueUsed(value, complexValueType, complexValueId));
                case StirProcessFlowBasedOnType.BASED_ON:
                    switch (bioreactorProperties.on?.type) {
                        case BasedOnType.Kla:
                            return checkComplexValueUsed(bioreactorProperties.on.kla, complexValueType, complexValueId);
                        case BasedOnType.Ppv:
                            return checkComplexValueUsed(bioreactorProperties.on.ppv, complexValueType, complexValueId);
                        default:
                            return false;
                    }
                case GasProcessFlowBasedOnType.BASED_ON_KLA:
                    return checkComplexValueUsed(bioreactorProperties.kla, complexValueType, complexValueId);
                default:
                    return false;
            }
        }
        case RecipeStepType.CONDITIONAL:
            return step.flows.some(({ condition }) => (isNotEmpty(condition?.customVariable) && condition?.customVariable === complexValueId) ||
                [condition?.value, condition?.maxValue].some((value) => checkComplexValueUsed(value, complexValueType, complexValueId)));
        case RecipeStepType.SET_DO_CONTROLLER: {
            const isDODurationUsed = checkComplexValueUsed(step.time, complexValueType, complexValueId);
            if (isDODurationUsed) {
                return true;
            }
            return step.steps.some((step) => {
                if (!step || step.type === DOControllerStepType.UNKNOWN) {
                    return false;
                }
                const additionalProperties = [];
                switch (step.type) {
                    case DOControllerStepType.STIR:
                        additionalProperties.push(step.minimumValue, step.maximumValue);
                        break;
                    case DOControllerStepType.GAS_RATIO:
                        additionalProperties.push(step.flowRate);
                        break;
                    case DOControllerStepType.GAS_SUPPLEMENT:
                        additionalProperties.push(step.minimumValue, step.maximumValue);
                }
                return [step.parameterP, step.parameterD, step.parameterI, ...additionalProperties].some((value) => checkComplexValueUsed(value, complexValueType, complexValueId));
            });
        }
        case RecipeStepType.SET_PH_CONTROLLER: {
            const isPhDurationUsed = checkComplexValueUsed(step.time, complexValueType, complexValueId);
            if (isPhDurationUsed) {
                return true;
            }
            return [step.topControlProperties, step.bottomControlProperties].some((control) => {
                switch (control?.controllerProperties.type) {
                    case PhControllerType.PID:
                        return [
                            control?.controllerProperties.parameterP,
                            control?.controllerProperties.parameterI,
                            control?.controllerProperties.parameterD,
                            control?.controllerProperties.deadband,
                            control?.controllerProperties.minFlowRate,
                            control?.controllerProperties.maxFlowRate,
                        ].some((value) => checkComplexValueUsed(value, complexValueType, complexValueId));
                    case PhControllerType.BOLUS:
                        return [
                            control?.controllerProperties.deadband,
                            control?.controllerProperties.relaxationTime,
                            control?.controllerProperties.volumeToAdd,
                        ].some((value) => checkComplexValueUsed(value, complexValueType, complexValueId));
                    default:
                        return false;
                }
            });
        }
        case RecipeStepType.LOOP: {
            const timeoutUsed = checkComplexValueUsed(step.condition?.timeout, complexValueType, complexValueId);
            if (timeoutUsed) {
                return true;
            }
            switch (step.condition?.type) {
                case LoopConditionType.ITERATIONS_COUNT:
                    return checkComplexValueUsed(step.condition.count, complexValueType, complexValueId);
                case LoopConditionType.CONDITION:
                    return [
                        step?.condition?.condition?.value,
                        step?.condition?.condition?.maxValue,
                    ].some((value) => checkComplexValueUsed(value, complexValueType, complexValueId));
                default:
                    return false;
            }
        }
        case RecipeStepType.TAKE_SAMPLE:
            switch (step.properties?.type) {
                case TakeSampleType.VOLUME_REDUCTION:
                    return checkComplexValueUsed(step.properties?.volume, complexValueType, complexValueId);
                case TakeSampleType.SAMPLING:
                    switch (step.properties?.properties?.type) {
                        case SamplingType.BY_VOLUME:
                            return checkComplexValueUsed(step.properties?.properties?.volume, complexValueType, complexValueId);
                        case SamplingType.BY_CELLS_NUMBER:
                            return checkComplexValueUsed(step.properties?.properties?.number, complexValueType, complexValueId);
                        default:
                            return false;
                    }
                default:
                    return false;
            }
        case RecipeStepType.BLEED:
            switch (step.properties?.type) {
                case BleedType.HOLD_CELL_DENSITY:
                    return [step?.properties?.density, step?.properties?.time].some((value) => checkComplexValueUsed(value, complexValueType, complexValueId));
                case BleedType.HOLD_VOLUME:
                    return [step?.properties?.volume, step?.properties?.time].some((value) => checkComplexValueUsed(value, complexValueType, complexValueId));
                case BleedType.REMOVE_CELL_FREE_LIQUID:
                    return (checkComplexValueUsed(step?.properties?.volume, complexValueType, complexValueId) ||
                        checkComplexValueUsedInTimeProfile(step?.properties?.timeProfile, complexValueType, complexValueId));
                case BleedType.REMOVE_CELL_CONCENTRATED_LIQUID:
                    switch (step?.properties?.properties?.type) {
                        case CellConcentratedLiquidType.VOLUME:
                            return (checkComplexValueUsed(step?.properties?.properties?.volume, complexValueType, complexValueId) ||
                                checkComplexValueUsedInTimeProfile(step?.properties?.properties?.timeProfile, complexValueType, complexValueId));
                        case CellConcentratedLiquidType.NUMBER_OF_CELLS:
                            return (checkComplexValueUsed(step?.properties?.properties?.numberOfCells, complexValueType, complexValueId) ||
                                checkComplexValueUsedInTimeProfile(step?.properties?.properties?.timeProfile, complexValueType, complexValueId));
                        default:
                            return false;
                    }
                default:
                    return false;
            }
        case RecipeStepType.MEDIA_EXCHANGE:
            switch (step.properties?.type) {
                case MediaExchangeType.CELL_FREE_LIQUID:
                    return checkComplexValueUsed(step.properties?.volume, complexValueType, complexValueId);
                case MediaExchangeType.CELL_CONCENTRATED:
                    switch (step.properties?.properties?.type) {
                        case CellConcentratedExchangeType.BY_VOLUME:
                            return checkComplexValueUsed(step.properties?.properties?.volume, complexValueType, complexValueId);
                        case CellConcentratedExchangeType.BY_CELL_NUMBER:
                            return checkComplexValueUsed(step.properties?.properties?.numberOfCells, complexValueType, complexValueId);
                        default:
                            return false;
                    }
                default:
                    return false;
            }
        default:
            return false;
    }
});
export const getRecipeStepsUsingGas = (steps, gasId) => {
    if (!isNotEmpty(gasId)) {
        return [];
    }
    return steps.filter((step) => {
        switch (step.type) {
            case RecipeStepType.GAS_BIOREACTOR:
                return gasId === step.gasId;
            case RecipeStepType.SET_PH_CONTROLLER:
                return [step.topControlProperties, step.bottomControlProperties].some((control) => {
                    if (!control?.type || control?.type === InterventionType.LIQUID) {
                        return false;
                    }
                    return control.gasId === gasId;
                });
            case RecipeStepType.SET_DO_CONTROLLER:
                return step.steps.some((step) => {
                    switch (step?.type) {
                        case DOControllerStepType.GAS_RATIO:
                            return gasId === step?.gasId || gasId === step?.secondGasId;
                        case DOControllerStepType.GAS_SUPPLEMENT:
                            return gasId === step?.gasId;
                        default:
                            return false;
                    }
                });
            default:
                return false;
        }
    });
};
export const getRecipeStepsUsingLiquids = (steps, liquidId) => {
    if (!isNotEmpty(liquidId)) {
        return [];
    }
    return steps.filter((step) => {
        switch (step.type) {
            case RecipeStepType.ADD_LIQUID:
                return liquidId === step.liquidId;
            case RecipeStepType.SET_PH_CONTROLLER:
                return [step.topControlProperties, step.bottomControlProperties].some((control) => {
                    if (!control?.type || control?.type === InterventionType.GAS) {
                        return false;
                    }
                    return control.liquidId === liquidId;
                });
            default:
                return false;
        }
    });
};
export const getLiquidsReferencedInComplexValues = (liquids, complexValueType, complexValueId) => {
    if (!isNotEmpty(complexValueId)) {
        return [];
    }
    return liquids.filter((liquid) => [liquid.ph, liquid.temperature, liquid.inoculumConcentration, liquid.bufferingCapacity].some((value) => checkComplexValueUsed(value, complexValueType, complexValueId)) ||
        liquid.components.some(({ concentration }) => checkComplexValueUsed(concentration, complexValueType, complexValueId)));
};
export const getCustomVariablesUsingComplexValue = (customVariables, complexValueId) => customVariables.filter(({ formula }) => {
    let values = [];
    switch (formula?.type) {
        case CustomVariableFormulaType.Value:
        case CustomVariableFormulaType.Exponential:
        case CustomVariableFormulaType.LogBase10:
        case CustomVariableFormulaType.Ln:
            values = [formula?.value];
            break;
        case CustomVariableFormulaType.Sum:
        case CustomVariableFormulaType.Difference:
        case CustomVariableFormulaType.Product:
        case CustomVariableFormulaType.Quotient:
        case CustomVariableFormulaType.Maximum:
        case CustomVariableFormulaType.Minimum:
        case CustomVariableFormulaType.Power:
        case CustomVariableFormulaType.AbsoluteDifference:
            values = [
                formula?.first,
                formula?.second,
            ];
            break;
        case CustomVariableFormulaType.ZScore:
            values = [
                formula?.referenceValue,
                formula?.optimum,
                formula?.spread,
            ];
            break;
        case CustomVariableFormulaType.SaturationScore:
            values = [
                formula?.referenceValue,
                formula?.referenceHigh,
                formula?.referenceLow,
                formula?.value50,
            ];
            break;
        case CustomVariableFormulaType.LogisticScore:
            values = [
                formula?.referenceValue,
                formula?.referenceHigh,
                formula?.referenceLow,
                formula?.sensitivity,
                formula?.threshold,
            ];
            break;
    }
    return values.some((value) => {
        const { type, of } = { ...value };
        if (type === CustomVariableValueType.Absolute) {
            return false;
        }
        const { type: parameterType, parameterId } = of;
        return parameterType === CustomVariableRelativeValueVariableType.RecipeParameter
            ? parameterId === complexValueId
            : false;
    });
});
export const getStepSubtitle = (step) => {
    switch (step.type) {
        case RecipeStepType.ADD_LIQUID:
            return findDictionaryItemByValue(addLiquidTypes, step.addLiquidTypeProperties?.type)?.label;
        case RecipeStepType.SET_TEMPERATURE:
        case RecipeStepType.SET_DO:
        case RecipeStepType.SET_PH:
            return findDictionaryItemByValue(operationTypes, step.properties?.type)?.label;
        case RecipeStepType.GAS_BIOREACTOR:
            return findDictionaryItemByValue(gasProcessFlowTypes, step.properties?.type)?.label;
        case RecipeStepType.STIR_BIOREACTOR:
            return findDictionaryItemByValue(stirProcessFlowTypes, step.properties?.type)?.label;
    }
};
export const getStepTitleAndColor = (stepsList, stepType, subtitle) => {
    const defaultState = { title: stepType, color: "#6F6F6F" };
    if (stepsList) {
        const allStepTypes = stepsList.reduce((arr, { variablesList }) => [...arr, ...variablesList], []);
        const existedStepType = allStepTypes.find(({ type }) => type === stepType);
        if (!existedStepType) {
            return defaultState;
        }
        else {
            const { name, color } = existedStepType;
            return { title: `${name}${subtitle ? ` - ${subtitle}` : ""}`, color };
        }
    }
    else {
        return defaultState;
    }
};
export const getUnitsCategory = (parameterTypes, units) => parameterTypes?.find(({ variablesList }) => variablesList.find(({ type }) => type === units));
export const getParameterUnitsOptions = (parameterTypes) => {
    const noUnitsDictionaryItem = findDictionaryItemByValue(recipeParameterUnitsType, NoUnitsType.NoUnits);
    return ((parameterTypes ?? [])
        .filter(({ category }) => category !== ParameterTypesCategoryType.NoUnits)
        .reduce((acc, { category, categoryName, variablesList }) => {
        const groupNameItem = {
            value: category,
            label: categoryName,
            disabled: true,
            isGroupName: true,
            groupId: category,
        };
        const variableOptions = variablesList.reduce((acc, { type }) => {
            const dictionaryUnits = findDictionaryItemByValue(recipeParameterUnitsType, type);
            if (dictionaryUnits) {
                acc.push({
                    ...dictionaryUnits,
                    groupId: category,
                    shouldHighlight: false,
                    searchVariants: parameterUnitsSearchVariants[type],
                });
            }
            return acc;
        }, []);
        return [...acc, groupNameItem, ...variableOptions];
    }, noUnitsDictionaryItem ? [{ ...noUnitsDictionaryItem, shouldHighlight: false }] : []) ?? []);
};
export const checkRecipeParameterIsCompleted = (parameter) => {
    const requiredRecipeParameterObjectKeys = ["name", "value", "units"];
    return requiredRecipeParameterObjectKeys.every((i) => isNotEmpty(parameter[i], true));
};
export const checkIsComplexStep = (step) => {
    if (!step) {
        return false;
    }
    return (step.type === RecipeStepType.CONCURRENT ||
        step.type === RecipeStepType.CONDITIONAL ||
        step.type === RecipeStepType.LOOP ||
        step.type === RecipeStepType.ALTERNATIVE);
};
export const getStepTrunks = (step) => {
    switch (step.type) {
        case RecipeStepType.CONCURRENT:
            return step.concurrentFlows;
        case RecipeStepType.CONDITIONAL:
            return step.flows;
        case RecipeStepType.LOOP:
            return [step.flow];
        case RecipeStepType.ALTERNATIVE:
            return step.flowProperties;
        default:
            return [];
    }
};
export const defineNewPreviousStepIdInTrunk = (selectedSteps, newPreviousStepIndex, steps) => {
    const newPreviousStepId = steps[newPreviousStepIndex]?.id;
    if (newPreviousStepId === undefined) {
        return undefined;
    }
    else if (selectedSteps.includes(newPreviousStepId)) {
        return defineNewPreviousStepIdInTrunk(selectedSteps, newPreviousStepIndex - 1, steps);
    }
    else {
        return newPreviousStepId;
    }
};
const findPathToStepInsideNestedTrunks = (idToFind, step, path, withSearchedStep) => {
    if (!checkIsComplexStep(step)) {
        return;
    }
    const trunks = getStepTrunks(step);
    const pathToStep = [...(path ?? []), step];
    for (const trunk of trunks) {
        const desiredStep = trunk.steps.find(({ id }) => id === idToFind);
        if (desiredStep) {
            return [...pathToStep, ...(withSearchedStep ? [desiredStep] : [])];
        }
        for (const step of trunk.steps) {
            if (checkIsComplexStep(step)) {
                const pathInTrunk = findPathToStepInsideNestedTrunks(idToFind, step, pathToStep, withSearchedStep);
                if (pathInTrunk) {
                    return pathInTrunk;
                }
            }
        }
    }
};
export const findPathToStep = (idToFind, steps, withDesiredStep) => {
    const stepOnMainTrunk = steps.find(({ id }) => id === idToFind);
    if (stepOnMainTrunk) {
        return withDesiredStep ? [stepOnMainTrunk] : [];
    }
    for (const step of steps) {
        if (checkIsComplexStep(step)) {
            const pathToStep = findPathToStepInsideNestedTrunks(idToFind, step, undefined, withDesiredStep);
            if (pathToStep) {
                return pathToStep;
            }
        }
    }
    return undefined;
};
const definePlusIconPlacementInPathToPreviousStepIdInsideNestedTrunks = (selectedSteps, plusIconPlacement, recipeSteps, pathToPreviousStepIdInsideNestedTrunks) => {
    const { previousStepId, complexStepAttrs } = plusIconPlacement;
    const firstStepToBeDeletedIdx = pathToPreviousStepIdInsideNestedTrunks.findIndex(({ id }) => selectedSteps.includes(id));
    const stepToPlusIconPlacement = pathToPreviousStepIdInsideNestedTrunks[firstStepToBeDeletedIdx - 1];
    if (!stepToPlusIconPlacement &&
        recipeSteps.find(({ id }) => id === pathToPreviousStepIdInsideNestedTrunks[firstStepToBeDeletedIdx]?.id)) {
        const stepsThatBeSaved = recipeSteps.filter(({ id }) => !selectedSteps.includes(id));
        return { previousStepId: stepsThatBeSaved[stepsThatBeSaved.length - 1]?.id };
    }
    if (stepToPlusIconPlacement?.id === complexStepAttrs?.stepId) {
        if (checkIsComplexStep(stepToPlusIconPlacement)) {
            const { id, type } = stepToPlusIconPlacement;
            const trunks = getStepTrunks(stepToPlusIconPlacement);
            const selectedTrunk = trunks.find(({ id }) => id === complexStepAttrs?.trunkId);
            if (selectedTrunk) {
                const previousStepIndex = selectedTrunk.steps.findIndex(({ id }) => id === previousStepId);
                return {
                    ...plusIconPlacement,
                    previousStepId: defineNewPreviousStepIdInTrunk(selectedSteps, previousStepIndex === -1 ? selectedTrunk.steps.length - 1 : previousStepIndex - 1, selectedTrunk.steps),
                };
            }
            return {
                previousStepId: undefined,
                complexStepAttrs: { stepId: id, trunkId: trunks[0]?.id, type },
            };
        }
    }
    if (stepToPlusIconPlacement) {
        if (checkIsComplexStep(stepToPlusIconPlacement)) {
            const { id, type } = stepToPlusIconPlacement;
            const trunks = getStepTrunks(stepToPlusIconPlacement);
            const trunkThatContainsFirstStepToDelete = trunks.find((trunk) => trunk.steps.find(({ id }) => id === pathToPreviousStepIdInsideNestedTrunks[firstStepToBeDeletedIdx]?.id));
            const trunkToPlusIconPlacement = trunkThatContainsFirstStepToDelete ?? trunks[0];
            return {
                previousStepId: defineNewPreviousStepIdInTrunk(selectedSteps, trunkToPlusIconPlacement.steps.length - 1, trunkToPlusIconPlacement.steps),
                complexStepAttrs: {
                    stepId: id,
                    trunkId: trunkToPlusIconPlacement.id,
                    type,
                },
            };
        }
    }
    return plusIconPlacement;
};
export const getNewPlusIconPlacementOnDeleteStep = (selectedSteps, recipeSteps, plusIconPlacement) => {
    const { previousStepId, complexStepAttrs } = plusIconPlacement;
    let newPlusIconPlacement = plusIconPlacement;
    if (
    // Current plus icon placement on main truck
    previousStepId !== undefined &&
        complexStepAttrs?.stepId === undefined &&
        selectedSteps.includes(previousStepId)) {
        const previousStepIndex = recipeSteps.findIndex(({ id }) => id === previousStepId);
        newPlusIconPlacement = {
            previousStepId: defineNewPreviousStepIdInTrunk(selectedSteps, previousStepIndex - 1, recipeSteps),
        };
    }
    else if (complexStepAttrs?.stepId !== undefined) {
        const pathToStep = findPathToStep(previousStepId ?? complexStepAttrs.stepId, recipeSteps, true);
        if (pathToStep) {
            newPlusIconPlacement = definePlusIconPlacementInPathToPreviousStepIdInsideNestedTrunks(selectedSteps, plusIconPlacement, recipeSteps, pathToStep);
        }
    }
    return newPlusIconPlacement;
};
export const flatSteps = (steps, filterCb) => steps.reduce((acc, step) => {
    if (checkIsComplexStep(step)) {
        if (!filterCb || filterCb(step)) {
            acc.push(step);
        }
        const trunks = getStepTrunks(step);
        const trunksSteps = trunks.reduce((acc, trunk) => [...acc, ...flatSteps(trunk.steps, filterCb)], []);
        return [...acc, ...trunksSteps];
    }
    else {
        if (!filterCb || filterCb(step)) {
            acc.push(step);
        }
        return acc;
    }
}, []);
export const getRecipeTimestamps = (steps) => steps
    .filter((step) => step.type === RecipeStepType.TIMESTAMP && isNotEmpty(step.text))
    .map((step) => {
    const { text, id } = step;
    return { label: text ?? "", value: { type: TimestampType.Custom, id } };
});
export const getPlusIconPlacementOnDeleteFlow = (plusIconPlacement, step, flowId, prevIndex) => {
    const { complexStepAttrs } = plusIconPlacement;
    if (complexStepAttrs?.stepId === step.id && complexStepAttrs.trunkId === flowId) {
        const trunks = getStepTrunks(step);
        const { steps, id } = prevIndex === -1 ? trunks[1] : trunks[prevIndex];
        return {
            previousStepId: steps[steps.length - 1]?.id,
            complexStepAttrs: { ...complexStepAttrs, trunkId: id },
        };
    }
    return plusIconPlacement;
};
export const defineSetAlternativeActiveFlowsPayloadOnOpenStep = (payload, recipeSteps, plusIconPlacement, alternativeStepActiveFlows) => {
    const stepIdToFindPlusIconPlacement = plusIconPlacement?.previousStepId ?? plusIconPlacement?.complexStepAttrs?.stepId;
    const pathToPlusIconPlacement = findPathToStep(stepIdToFindPlusIconPlacement, recipeSteps, true);
    const alternativeStepsToChangeActiveFlow = [];
    let newPlusIconPlacement;
    Object.entries(payload).forEach((entry) => {
        if (entry[0] === RecipeDialogType.STEP && entry[1].isOpen) {
            const step = entry[1].step;
            if (!step) {
                return;
            }
            const pathToStep = findPathToStep(step.id, recipeSteps, true);
            pathToStep?.forEach((step, index) => {
                if (step.type === RecipeStepType.ALTERNATIVE) {
                    const trunkToSet = step.flowProperties.find(({ steps }) => steps.find(({ id }) => id === pathToStep[index + 1]?.id));
                    if (trunkToSet && alternativeStepActiveFlows[step.id] !== trunkToSet?.id) {
                        alternativeStepsToChangeActiveFlow.push({ step, trunkId: trunkToSet.id });
                        if (!newPlusIconPlacement && pathToPlusIconPlacement?.some(({ id }) => id === step.id)) {
                            newPlusIconPlacement = {
                                previousStepId: trunkToSet.steps[trunkToSet.steps.length - 1]?.id,
                                complexStepAttrs: {
                                    stepId: step.id,
                                    type: step.type,
                                    trunkId: trunkToSet.id,
                                },
                            };
                        }
                    }
                }
            }, []);
        }
    });
    if (!alternativeStepsToChangeActiveFlow.length) {
        return;
    }
    return {
        payload: alternativeStepsToChangeActiveFlow.reduce((acc, { step, trunkId }) => ({ ...acc, [step.id]: trunkId }), {}),
        plusIconPlacement: newPlusIconPlacement,
    };
};
export const setRecipeStepPreviewRef = (stepId, stepPreviewRefs) => (element) => {
    if (element && stepPreviewRefs.current) {
        stepPreviewRefs.current[stepId] = element;
    }
};
export const prepareStepsOnSelect = (step, selectedStepIds, steps, currentStepSelected) => {
    if (currentStepSelected) {
        const stepsToUnselect = [step.id, ...(findPathToStep(step.id, steps) ?? []).map(({ id }) => id)];
        if (checkIsComplexStep(step)) {
            const nestedStepIds = flatSteps([step]).map(({ id }) => id);
            stepsToUnselect.push(...nestedStepIds);
        }
        return selectedStepIds.filter((id) => !stepsToUnselect.includes(id));
    }
    else {
        if (checkIsComplexStep(step)) {
            const idsToSelect = flatSteps([step]).map(({ id }) => id);
            return [...selectedStepIds.filter((id) => !idsToSelect.includes(id)), ...idsToSelect];
        }
        else {
            return [...selectedStepIds, step.id];
        }
    }
};
export const getConditionValuesText = (condition, parameters, customVariables, processVariables) => {
    const { type, variable, customVariable, value, maxValue } = { ...condition };
    if (!type || (!variable && !customVariable)) {
        return;
    }
    const customVariableEntity = customVariables?.find(({ id }) => id === customVariable);
    let variableName = variable
        ? getSelectedProcessVariable(processVariables ?? [], variable)?.name
        : customVariableEntity?.name;
    const operationTypeLabel = findDictionaryItemByValue(conditionOperationTypes, type)?.label;
    if (!variableName || !operationTypeLabel) {
        return;
    }
    let valueLabel = getComplexValueStepText({ value, parameters, customVariables }) ?? "";
    if (customVariable) {
        variableName = `{${variableName}}`;
        if ([ComplexValueParameterType.VALUES, ComplexValueParameterType.ANY_UNIT_VALUE].includes(value?.type)) {
            valueLabel = `
            ${value?.value}
            ${parseComplexUnitToHtml(customVariableEntity?.formula?.configuredUnits?.complexValue)?.html}
          `;
        }
    }
    if (!valueLabel) {
        return;
    }
    if (type === ConditionOperationType.OUT_OF_RANGE || type === ConditionOperationType.IN_RANGE) {
        const maxValueLabel = getComplexValueStepText({ value: maxValue, parameters, customVariables });
        if (!maxValueLabel) {
            return;
        }
        switch (type) {
            case ConditionOperationType.IN_RANGE:
                return (React.createElement(React.Fragment, null,
                    variableName,
                    " \u2265 ",
                    valueLabel,
                    " & \u2264 ",
                    React.createElement(HtmlText, { text: maxValueLabel })));
            case ConditionOperationType.OUT_OF_RANGE:
                return (React.createElement(React.Fragment, null,
                    variableName,
                    " \u2264 ",
                    valueLabel,
                    " & \u2265 ",
                    React.createElement(HtmlText, { text: maxValueLabel })));
            default:
                return;
        }
    }
    else {
        switch (type) {
            case ConditionOperationType.GREATER_THAN:
                return (React.createElement(React.Fragment, null,
                    variableName,
                    " > ",
                    React.createElement(HtmlText, { text: valueLabel })));
            case ConditionOperationType.GREATER_THAN_OR_EQUAL:
                return (React.createElement(React.Fragment, null,
                    variableName,
                    " \u2265 ",
                    React.createElement(HtmlText, { text: valueLabel })));
            case ConditionOperationType.LESS_THAN:
                return (React.createElement(React.Fragment, null,
                    variableName,
                    " < ",
                    React.createElement(HtmlText, { text: valueLabel })));
            case ConditionOperationType.LESS_THAN_OR_EQUAL:
                return (React.createElement(React.Fragment, null,
                    variableName,
                    " \u2264 ",
                    React.createElement(HtmlText, { text: valueLabel })));
            default:
                return;
        }
    }
};
export const getSelectedVariable = (customVariables, processVariables, customVariable, variable) => {
    if (isNotEmpty(customVariable)) {
        const customVariableEntity = customVariables?.find(({ id }) => id === customVariable);
        if (customVariableEntity) {
            return {
                key: customVariableEntity.id,
                name: customVariableEntity?.name ?? undefined,
                units: customVariableEntity?.formula?.configuredUnits?.value ?? undefined,
            };
        }
    }
    else {
        return getSelectedProcessVariable(processVariables, variable);
    }
};
export const getRecipeParametersToOptimizeList = ({ parameters, steps }, usedParameters) => getRecipeParametersAndAlternativeStepsOptions(parameters
    .filter((parameter) => checkRecipeParameterIsCompleted(parameter) &&
    !usedParameters.find((optimizationParameter) => optimizationParameter.type === OptimizationParameterParameterType.CELL_MODEL_OR_RECIPE &&
        `${optimizationParameter.parameterId}` === `${parameter.id}`))
    ?.map(({ name, id }) => ({
    value: { type: OptimizationParameterParameterType.CELL_MODEL_OR_RECIPE, id },
    label: name || "",
})), flatSteps(steps)
    .filter((step) => step.type === RecipeStepType.ALTERNATIVE &&
    step.flowProperties.length > 1 &&
    !usedParameters.find((optimizationParameter) => optimizationParameter.type === OptimizationParameterParameterType.ALTERNATIVE_FLOW &&
        optimizationParameter.stepId === step.id))
    .map((step) => ({
    value: { type: OptimizationParameterParameterType.ALTERNATIVE_FLOW, id: step.id },
    label: step.name || "",
})));
export const getSelectedRecipeParameterToOptimize = (target, parametersOverrides, selectedId) => {
    const parameter = target?.state?.parameters?.find(({ id }) => `${selectedId}` === `${id}`);
    if (parameter) {
        let value = parameter.value ?? "";
        if (parametersOverrides) {
            const alteredParameter = parametersOverrides.find(({ type, id }) => type === GlobalEntityType.Recipe && `${selectedId}` === `${id}`);
            if (alteredParameter) {
                value = alteredParameter.parameterValue ?? "";
            }
        }
        return {
            type: OptimizationParameterParameterType.CELL_MODEL_OR_RECIPE,
            name: parameter.name ?? "",
            value,
            units: (parameter?.units === NoUnitsType.NoUnits
                ? ""
                : findDictionaryItemByValue(recipeParameterUnitsType, parameter?.units, getDefaultUnitsPrefix)
                    ?.label || ""),
        };
    }
};
export const getAlternativeStepFlowName = ({ isDefault }, index) => (isDefault ? "Default" : `Flow ${index}`);
export const getFlowNameById = (flowProperties, flowId) => {
    for (const index in flowProperties) {
        const flow = flowProperties[index];
        if (flow.id === flowId) {
            return getAlternativeStepFlowName(flow, index);
        }
    }
};
export const getSelectedRecipeAlternativeStepToOptimize = (target, selectedId) => {
    const step = flatSteps(target?.state?.steps || []).find(({ id, type }) => type === RecipeStepType.ALTERNATIVE && `${selectedId}` === `${id}`);
    if (step) {
        return {
            type: OptimizationParameterParameterType.ALTERNATIVE_FLOW,
            name: step.name ?? "",
            flows: [...step.flowProperties].map((flow, index) => ({
                id: flow.id,
                name: getAlternativeStepFlowName(flow, index),
            })) || [],
        };
    }
};
export const getAlternativeStepFlowsList = (recipes, stepName) => recipes.reduce((acc, { state: { steps = [] } = {} }) => {
    flatSteps(steps).forEach((step) => {
        if (step.type === RecipeStepType.ALTERNATIVE && step.name === stepName) {
            [...step.flowProperties].forEach((flow, index) => {
                if (!acc[index]) {
                    acc.push(getAlternativeStepFlowName(flow, index));
                }
            });
        }
    });
    return acc;
}, []);
export const referencedStepMapper = (step, stepsList) => {
    let payload = { isOpen: true };
    let type = RecipeDialogType.STEP;
    switch (step.type) {
        case RecipeStepType.CONDITIONAL:
            payload = {
                ...payload,
                conditionalStepId: step.id,
                flow: step.flows[0],
                flowIndex: 0,
            };
            type = RecipeDialogType.CONDITIONAL_FLOW;
            break;
        default:
            payload.step = step;
    }
    return {
        name: getStepTitleAndColor(stepsList, step.type, getStepSubtitle(step))?.title || "",
        type,
        payload,
    };
};
